import { useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import useShrineNameSearch from "./useShrineNameSearch";
import PREFECTURES from "../../constants/prefectures";

const SERIF = '"Hiragino Mincho ProN", "Yu Mincho", serif';
const WASHI = "#ece5d4";
const WASHI_DIM = "rgba(222,215,198,0.55)";
const WASHI_FAINT = "rgba(222,215,198,0.4)";
const GOLD = "#d4b46e";
const GOLD_TEXT = "#e6d3a3";

const MAX_RESULTS = 8;

const INPUT_SX = {
  "& .MuiInputBase-root": {
    fontFamily: SERIF,
    color: WASHI,
    letterSpacing: 1,
    bgcolor: "rgba(212,180,110,0.04)",
    borderRadius: 0,
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(212,180,110,0.35)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(224,201,143,0.7)" },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: `${GOLD} !important`, borderWidth: 1 },
  "& input::placeholder": { color: WASHI_DIM, opacity: 1 },
  // 都道府県の select（native）。墨の上で読めるように
  "& select": { fontFamily: SERIF, color: WASHI, letterSpacing: 1, py: 1.05 },
  "& select option": { color: "#12100d", bgcolor: "#ece5d4" },
  "& .MuiNativeSelect-icon": { color: WASHI_DIM },
};

const DOOR_SX = {
  fontFamily: SERIF,
  fontWeight: 600,
  px: 6,
  py: 1.4,
  fontSize: "0.95rem",
  letterSpacing: 5,
  textIndent: 5,
  color: GOLD_TEXT,
  bgcolor: "rgba(212,180,110,0.05)",
  border: "1px solid rgba(212,180,110,0.5)",
  borderRadius: 0,
  boxShadow: "0 0 24px rgba(212,180,110,0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    bgcolor: "rgba(212,180,110,0.12)",
    borderColor: "rgba(224,201,143,0.9)",
    color: "#f2e5c0",
  },
  "&.Mui-disabled": { color: "rgba(230,211,163,0.3)", borderColor: "rgba(212,180,110,0.18)" },
};

/** 今の絞り込みでの総数。「全国に、五百二社。」「京都府に、十四社。」 */
function Count({ name, prefecture, city, total, shown }) {
  if (!name || typeof total !== "number") return null;
  const where = prefecture ? `${prefecture}${city}に` : "全国に";
  const n = total.toLocaleString("ja-JP");
  const needPlace = total > shown && !prefecture;
  const needCity = total > shown && prefecture && !city;
  return (
    <Typography
      data-testid="kioku-count"
      sx={{ fontFamily: SERIF, color: WASHI_DIM, fontSize: "0.75rem", letterSpacing: 1.2, lineHeight: 1.9, mt: 1, px: 0.5 }}
    >
      {total === 0 ? `${where}、見つかりませんでした。別の字で。` : `${where}、${n}社。`}
      {needPlace && <Box component="span" sx={{ color: WASHI_FAINT, ml: 1 }}>土地で絞ると、見つけやすくなります。</Box>}
      {needCity && <Box component="span" sx={{ color: WASHI_FAINT, ml: 1 }}>市区町村でも絞れます。</Box>}
    </Typography>
  );
}

/**
 * 記憶の参拝 — これまでに参った神社を数社、思い出して選ぶ。
 *
 * 入口の決定（骨 v3 #8）: 入れた直後にこれを出し、最初の30秒で「同じ神社に参った人 N 人」を見せる。
 * 誰にでも材料がある（初詣・七五三・氏神さま・修学旅行）。位置も時刻も聞かない。
 * ここでは人と人の縁を語らない。語るのは親（TopPage）側の一行だけ。
 *
 * 2026-10-14: 名前だけでは同名社が多すぎる（八幡神社 3,022・愛宕神社 502・愛知県の神明社だけで 458）ので、
 * 都道府県 → 市区町村で絞れるようにした。総数を一行で見せ、多いときは土地で絞るよう促す。
 */
export default function KiokuSanpai({ fixed = [], onDone, onCancel, compact = false, busy = false }) {
  const [text, setText] = useState("");
  const [prefecture, setPrefecture] = useState("");
  const [city, setCity] = useState("");
  const [picked, setPicked] = useState([]);
  const { results, searching, total } = useShrineNameSearch(text, { prefecture, city, max: MAX_RESULTS });

  // fixed = すでに記した社（visits は消せないので × は付けない）
  const has = (id) => picked.some((p) => p.shrineId === id) || fixed.some((f) => f.shrineId === id);
  const add = (s) => {
    if (has(s.shrineId)) return;
    setPicked((prev) => [...prev, s]);
    setText("");
  };
  const remove = (id) => setPicked((prev) => prev.filter((p) => p.shrineId !== id));

  const visible = results.filter((r) => !has(r.shrineId));
  const name = text.trim();

  return (
    <Box>
      {!compact && (
        <>
          <Typography
            sx={{
              fontFamily: SERIF,
              textAlign: "center",
              fontSize: { xs: "1.15rem", sm: "1.3rem" },
              letterSpacing: 2,
              lineHeight: 2.1,
              color: WASHI,
              mb: 1,
            }}
          >
            これまでに参った神社を、
            <br />
            思い出せるだけ。
          </Typography>
          <Typography
            sx={{
              fontFamily: SERIF,
              textAlign: "center",
              fontSize: "0.8rem",
              letterSpacing: 1.5,
              lineHeight: 2.2,
              color: WASHI_DIM,
              mb: 3.5,
            }}
          >
            初詣、七五三、氏神さま、修学旅行の伊勢。
          </Typography>
        </>
      )}

      <TextField
        fullWidth
        size="small"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="神社の名前"
        inputProps={{ "aria-label": "神社の名前", autoComplete: "off" }}
        sx={INPUT_SX}
      />

      {/* 土地で絞る（都道府県 → 市区町村）。名前を入れてから現れる */}
      {name && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <TextField
            select
            size="small"
            value={prefecture}
            onChange={(e) => {
              setPrefecture(e.target.value);
              setCity("");
            }}
            slotProps={{ select: { native: true }, htmlInput: { "aria-label": "都道府県" } }}
            sx={{ ...INPUT_SX, minWidth: 136, flex: prefecture ? "0 0 auto" : 1 }}
          >
            <option value="">どこの？</option>
            {PREFECTURES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </TextField>
          {prefecture && (
            <TextField
              fullWidth
              size="small"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="市区町村（任意）"
              inputProps={{ "aria-label": "市区町村", autoComplete: "off" }}
              sx={INPUT_SX}
            />
          )}
        </Stack>
      )}

      <Count name={name} prefecture={prefecture} city={city.trim()} total={total} shown={MAX_RESULTS} />

      {/* 検索結果 */}
      {(visible.length > 0 || searching) && (
        <Stack spacing={0} sx={{ mt: 1, borderTop: "1px solid rgba(212,180,110,0.18)" }}>
          {visible.map((s) => (
            <Box
              key={s.shrineId}
              component="button"
              type="button"
              onClick={() => add(s)}
              sx={{
                all: "unset",
                cursor: "pointer",
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 2,
                px: 1.5,
                py: 1.2,
                borderBottom: "1px solid rgba(212,180,110,0.12)",
                "&:hover": { bgcolor: "rgba(212,180,110,0.08)" },
              }}
            >
              <Typography sx={{ fontFamily: SERIF, color: WASHI, letterSpacing: 1, fontSize: "0.95rem" }}>
                {s.name}
              </Typography>
              <Typography sx={{ fontFamily: SERIF, color: WASHI_DIM, fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                {s.city || s.prefecture}
              </Typography>
            </Box>
          ))}
          {searching && visible.length === 0 && (
            <Typography sx={{ fontFamily: SERIF, color: WASHI_DIM, fontSize: "0.75rem", px: 1.5, py: 1.2 }}>
              …
            </Typography>
          )}
        </Stack>
      )}
      {name && !searching && visible.length === 0 && typeof total !== "number" && (
        <Typography sx={{ fontFamily: SERIF, color: WASHI_DIM, fontSize: "0.75rem", px: 1.5, py: 1.2 }}>
          見つかりませんでした。別の字で。
        </Typography>
      )}

      {/* 選んだ社（fixed はすでに記した社） */}
      {(picked.length > 0 || fixed.length > 0) && (
        <Stack direction="row" flexWrap="wrap" sx={{ mt: 2.5, gap: 1 }}>
          {fixed.map((s) => (
            <Box
              key={`fixed-${s.shrineId}`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1.5,
                py: 0.6,
                border: "1px solid rgba(212,180,110,0.22)",
                bgcolor: "rgba(212,180,110,0.03)",
              }}
            >
              <Typography sx={{ fontFamily: SERIF, color: WASHI_DIM, fontSize: "0.85rem", letterSpacing: 1 }}>
                {s.name || s.shrineId}
              </Typography>
            </Box>
          ))}
          {picked.map((s) => (
            <Box
              key={s.shrineId}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.6,
                border: "1px solid rgba(212,180,110,0.45)",
                bgcolor: "rgba(212,180,110,0.06)",
              }}
            >
              <Typography sx={{ fontFamily: SERIF, color: GOLD_TEXT, fontSize: "0.85rem", letterSpacing: 1 }}>
                {s.name}
              </Typography>
              <Box
                component="button"
                type="button"
                aria-label={`${s.name}を外す`}
                onClick={() => remove(s.shrineId)}
                sx={{
                  all: "unset",
                  cursor: "pointer",
                  color: WASHI_DIM,
                  fontSize: "0.8rem",
                  lineHeight: 1,
                  px: 0.3,
                  "&:hover": { color: WASHI },
                }}
              >
                ×
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      <Stack spacing={1.5} alignItems="center" sx={{ mt: 4 }}>
        {/* 宿主の言い方「お上がりした」を正とし、括弧で標準語を添える（2026-10-15 決定）。
            「参った」は「降参した」とも読めるので使わない */}
        <Button
          size="large"
          sx={{ ...DOOR_SX, px: { xs: 3.5, sm: 5 }, letterSpacing: 3, textIndent: 3, whiteSpace: "nowrap" }}
          disabled={picked.length === 0 || busy}
          onClick={() => onDone?.(picked)}
        >
          {busy ? "記しています" : "お上がりした（参拝した）"}
        </Button>
        {onCancel && (
          <Button
            variant="text"
            size="small"
            onClick={onCancel}
            sx={{ color: WASHI_DIM, fontSize: "0.78rem", fontFamily: SERIF, letterSpacing: 2, "&:hover": { color: WASHI } }}
          >
            {compact ? "やめる" : "あとで"}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
